import { lazy } from 'react';
import { Navigate, useLocation, type RouteObject } from 'react-router-dom';

import {
  GoogleCallbackPage,
  LoginPage,
  RegisterPage,
  SetPasswordPage,
} from '@/pages/auth';
import ToolEmbedPage from '@/pages/tools';
import { useIsNativeSession } from '@/store/auth';
import { PlanningLayoutRoute } from './guards/PlanningLayoutRoute';
import { BareProtectedRoute, ProtectedRoute } from './guards/ProtectedRoute';
import { FullWidthLayoutRoute } from './guards/FullWidthLayoutRoute';

// Все страницы грузятся лениво — стартовый бандл маленький, первый показ быстрый
// независимо от того, куда зашёл пользователь. Сразу после первого рендера
// prefetchRoutes() в простое подтягивает чанки остальных страниц в фон, чтобы
// последующие переходы были мгновенными, без полноэкранного лоадера.
const pageLoaders = {
  tasks: () => import('@/pages/tasks'),
  tasksV2: () => import('@/pages/tasks-v2'),
  taskDetailV2: () => import('@/pages/task-detail-v2'),
  chats: () => import('@/pages/chats'),
  chatsV2: () => import('@/pages/chats-v2'),
  projectsList: () => import('@/pages/projects'),
  projectDetail: () => import('@/pages/projects/detail'),
  groups: () => import('@/pages/groups'),
  disk: () => import('@/pages/disk'),
  directories: () => import('@/pages/directories'),
  contactCard: () => import('@/pages/contact-card'),
  dealCard: () => import('@/pages/deal-card'),
  finance: () => import('@/pages/finance'),
  payments: () => import('@/pages/payments'),
  pulse: () => import('@/pages/pulse'),
  taskView: () => import('@/pages/task-view'),
  taskRedesign: () => import('@/pages/task-redesign'),
  planning: () => import('@/pages/planning'),
  planningMobile: () => import('@/pages/planning/ui/PlanningMobilePage'),
  notifications: () => import('@/pages/notifications'),
  callsOpen: () => import('@/pages/calls-open'),
  team: () => import('@/pages/team'),
  profile: () => import('@/pages/profile'),
  products: () => import('@/pages/products'),
  events: () => import('@/pages/events'),
  adminPhase0: () => import('@/pages/admin-phase0'),
  projectsV2: () => import('@/pages/projects-v2'),
  projectV2Detail: () => import('@/pages/projects-v2/ProjectV2DetailPage'),
  objectTypes: () => import('@/pages/object-types'),
  crmV2: () => import('@/pages/crm-v2'),
  notFound: () => import('@/pages/not-found'),
};

const Tasks = lazy(pageLoaders.tasks);
const TasksV2 = lazy(pageLoaders.tasksV2);
const TaskDetailV2 = lazy(pageLoaders.taskDetailV2);
const Chats = lazy(pageLoaders.chats);
const ChatsV2 = lazy(pageLoaders.chatsV2);
const ProjectsList = lazy(pageLoaders.projectsList);
const ProjectDetail = lazy(pageLoaders.projectDetail);
const Groups = lazy(pageLoaders.groups);
const Disk = lazy(pageLoaders.disk);
const Directories = lazy(pageLoaders.directories);
const ContactCard = lazy(pageLoaders.contactCard);
const DealCardPage = lazy(pageLoaders.dealCard);
const Finance = lazy(pageLoaders.finance);
const Payments = lazy(pageLoaders.payments);
const Pulse = lazy(pageLoaders.pulse);
const TaskView = lazy(pageLoaders.taskView);
const TaskRedesignPage = lazy(pageLoaders.taskRedesign);
const PlanningPage = lazy(pageLoaders.planning);
const PlanningMobilePage = lazy(pageLoaders.planningMobile);
const NotificationsPage = lazy(pageLoaders.notifications);
const CallsOpenPage = lazy(pageLoaders.callsOpen);
const TeamPage = lazy(pageLoaders.team);
const ProfilePage = lazy(pageLoaders.profile);
const ProductsPage = lazy(pageLoaders.products);
const EventsPage = lazy(pageLoaders.events);
const AdminPhase0Page = lazy(pageLoaders.adminPhase0);
const ProjectsV2Page = lazy(pageLoaders.projectsV2);
const ProjectV2DetailPage = lazy(pageLoaders.projectV2Detail);
const ObjectTypesPage = lazy(pageLoaders.objectTypes);
const CrmV2Page = lazy(pageLoaders.crmV2);
const NotFound = lazy(pageLoaders.notFound);

let routesPrefetched = false;

/**
 * Прогрев чанков всех страниц в фоне — вызывается один раз в простое после
 * первого рендера (см. AppRouter). Идемпотентно. Браузер кеширует модуль по
 * спецификатору импорта, тот же чанк использует lazy(), поэтому к моменту
 * перехода страница уже готова и Suspense-fallback («Загрузка...») не мелькает.
 */
export function prefetchRoutes() {
  if (routesPrefetched) {
    return;
  }
  routesPrefetched = true;
  for (const load of Object.values(pageLoaders)) {
    void load().catch(() => undefined);
  }
}

/**
 * Редирект со стартового экрана на задачи нового бэка с сохранением query.
 * Голый `<Navigate to="/tasks-v2">` терял `?view=`, а мобильная навигация
 * ведёт именно так (`/?view=eisenhower`) — кнопка режима молча открывала
 * календарь вместо запрошенного режима.
 */
function TasksV2Redirect() {
  const { search } = useLocation();

  return <Navigate to={{ pathname: '/tasks-v2', search }} replace />;
}

/**
 * Чаты нативной сессии живут на едином ядре `/v2/chats` (раздел chats-v2):
 * legacy-источники прежнего экрана на новом бэке отвечают 404. У сессии
 * Bitrix24 остаётся прежний экран — это и откат, пока прод на старом бэке.
 */
function ChatsEntry() {
  const isNativeSession = useIsNativeSession();
  const { search } = useLocation();

  return isNativeSession ? (
    <Navigate to={{ pathname: '/chats-v2', search }} replace />
  ) : (
    <Chats />
  );
}

function TaskOverlay() {
  return (
    <div className="fixed inset-0 z-[999] bg-[#0B1A2A]">
      <TaskView />
    </div>
  );
}

/**
 * Оверлей детальной страницы (задача или карточка проекта) поверх той страницы,
 * с которой её открыли. Слой `fixed inset-0` кладёт её модалкой на весь экран, а
 * фон под ним остаётся смонтированным (фильтры списка, скролл, состояние вкладок
 * сохраняются). Активируется из AppRouter, когда есть фоновая локация; закрытие
 * (крестик, «назад» в UI или в браузере) возвращает на фон без перезагрузки.
 *
 * z-index — «страничный» (z-40), НЕ модальный: глобальные модалки (выбор
 * действия, продление, результат, автозакрытие и т.п.) портятся в body со своими
 * z (50…9999) и должны открываться ПОВЕРХ. Фон под оверлеем AppRouter прячет
 * через visibility, поэтому низкого z достаточно и фоновые навбар/шапки не
 * просвечивают. См. AppRouter.
 */
function ModalLayer({
  layout: Layout = ProtectedRoute,
}: {
  layout?: typeof ProtectedRoute;
}) {
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto">
      <Layout />
    </div>
  );
}

/** Маршруты в режиме оверлея (см. AppRouter). Пути те же, что у обычных
 *  маршрутов ниже, которые остаются fallback'ом для прямого захода/перезагрузки. */
export const modalRoutes: RouteObject[] = [
  {
    // Карточка задачи сверстана во всю ширину (свой фон, свой h-screen) — тот
    // же лэйаут без AppLayout-container, что у /finance, /edo, /contacts/:id.
    path: '/tasks-new/:id?',
    element: <ModalLayer layout={FullWidthLayoutRoute} />,
    children: [{ index: true, element: <TaskRedesignPage /> }],
  },
  {
    // Та же карточка задачи на новом бэке — и лэйаут тот же, во всю ширину.
    path: '/tasks-v2/:id',
    element: <ModalLayer layout={FullWidthLayoutRoute} />,
    children: [{ index: true, element: <TaskDetailV2 /> }],
  },
  {
    // Карточка проекта оформлена как карточка задачи — во всю ширину, со своей
    // тёмной шапкой, — поэтому и лэйаут у неё тот же, без AppLayout-container.
    path: '/projects/:entityTypeId/:id',
    element: <ModalLayer layout={FullWidthLayoutRoute} />,
    children: [{ index: true, element: <ProjectDetail /> }],
  },
];

export const routes: RouteObject[] = [
  // Публичные маршруты — вне ProtectedRoute, иначе редирект на /login зациклится.
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  // Пароль по инвайту: токен приходит ссылкой из письма, `?token=…`.
  { path: '/set-password', element: <SetPasswordPage /> },
  // Возврат из Google после согласия. Адрес один в один совпадает с тем, что
  // прописан в Google, — менять только вместе с devops.
  { path: '/auth/google/callback', element: <GoogleCallbackPage /> },

  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      // Стартовый экран — задачи нового бэка. Редирект стоит выше <Tasks />,
      // чтобы старый список не монтировался и не успевал сходить в legacy-API.
      { index: true, element: <TasksV2Redirect /> },
      // Экран задач старого бэка остаётся ради мобильного оверлея задачи:
      // на него ведёт список групп (MobileGroupsListV2). Сам список рисуется
      // фоном, поверх — <TaskOverlay /> из Outlet.
      {
        path: 'tasks/:id',
        element: <Tasks />,
        children: [{ index: true, element: <TaskOverlay /> }],
      },
    ],
  },

  // Задачи на новом бэке. Отдельный маршрут, пока основной экран сидит на
  // legacy-группе /tasks (переезд — фаза 4–5, docs/v2-migration-plan.md).
  {
    path: '/tasks-v2',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <TasksV2 /> }],
  },
  // Детальная задача нового бэка. Живёт параллельно с /tasks-new/:id: под
  // нативной сессией переходы ведут сюда (см. entities/task/model/task-route).
  {
    path: '/tasks-v2/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <TaskDetailV2 /> }],
  },

  {
    path: '/chats',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ChatsEntry /> }],
  },
  // Редизайн чатов («Чаты редизайн»): свой полноширинный каркас со списком,
  // лентой и полем ввода — как у Финансов и Пульса.
  {
    path: '/chats-v2',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <ChatsV2 /> }],
  },
  {
    path: '/groups',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Groups /> }],
  },
  {
    path: '/groups/:projectId',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Groups /> }],
  },
  {
    path: '/disk',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Disk /> }],
  },
  {
    path: '/directories',
    element: <ProtectedRoute />,
    children: [
      { index: true, element: <Navigate to="/directories/contacts" replace /> },
      { path: ':type', element: <Directories /> },
    ],
  },
  // Карточка контакта (прототипы «Карточка контакта» и «Карточка контакта
  // мобайл»). Своя тёмная шапка и лента вкладок, поэтому лэйаут без глобального
  // Header и без ограничения ширины — как у «Финансов» и «ЭДО».
  // `/contacts/demo` открывает карточку целиком на данных прототипа.
  {
    path: '/contacts/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <ContactCard /> }],
  },
  // Карточки сделки и лида (перенос из чекерборда, widgets/deal-card): общий
  // экран на две сущности, данные — из Справочников. Лэйаут тот же, что у
  // карточки контакта: своя тёмная шапка, футер этапов во всю ширину.
  {
    path: '/deals/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <DealCardPage entity="deals" /> }],
  },
  {
    path: '/leads/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <DealCardPage entity="leads" /> }],
  },
  // Раздел «Финансы» в сайдбаре (прототип «Модуль казначейства»): дашборд,
  // платёжный календарь, воронка оплат, прогноз, разрыв, кассы, банк.
  {
    path: '/finance',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <Finance /> }],
  },
  // Раздел «ЭДО» в сайдбаре: договоры, конструктор, шаблоны (прототип «Модуль оплат»).
  {
    path: '/edo',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <Payments /> }],
  },
  // Раздел «Операционный пульс» в сайдбаре (прототип «Пульс — Операции»):
  // вкладки Проекты и Отделы, боковые панели разбора.
  {
    path: '/pulse',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <Pulse /> }],
  },
  {
    path: '/projects',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ProjectsList /> }],
  },
  {
    path: '/projects/:entityTypeId/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <ProjectDetail /> }],
  },
  {
    path: '/tools/:toolId',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ToolEmbedPage /> }],
  },
  {
    path: '/tasks-new/:id?',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <TaskRedesignPage /> }],
  },
  {
    path: '/planning',
    element: <PlanningLayoutRoute />,
    children: [{ index: true, element: <PlanningPage /> }],
  },
  {
    path: '/planning/mobile',
    element: <BareProtectedRoute />,
    children: [{ index: true, element: <PlanningMobilePage /> }],
  },
  // «Войти через DOIT» со страницы видеозвонка: выдать platform-token и вернуть.
  {
    path: '/calls/open',
    element: <BareProtectedRoute />,
    children: [{ index: true, element: <CallsOpenPage /> }],
  },
  {
    path: '/planning/:entityTypeId/:id',
    element: <PlanningLayoutRoute />,
    children: [{ index: true, element: <PlanningPage /> }],
  },

  // Экраны нового бэка, у которых на legacy не было аналога: уведомления
  // (/v2/notifications*), структура компании с загрузкой рабочих групп
  // (/v2/departments*, /v2/work-groups*) и профиль с навыками (/v2/users*).
  {
    path: '/notifications',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <NotificationsPage /> }],
  },
  {
    path: '/team',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <TeamPage /> }],
  },
  {
    path: '/profile',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ProfilePage /> }],
  },
  {
    path: '/products',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ProductsPage /> }],
  },
  {
    path: '/events',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <EventsPage /> }],
  },
  // Служебный экран переезда: ключ X-Admin-Key вводится на самой странице,
  // в бандл он не попадает.
  {
    path: '/admin/phase0',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <AdminPhase0Page /> }],
  },
  // Объекты нового бэка (`/v2/projects`) и справочник их типов. Это НЕ старый
  // дашборд `/projects`: тот сидит на битриксовых смарт-процессах.
  {
    path: '/projects-v2',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ProjectsV2Page /> }],
  },
  {
    // Карточка объекта оформлена как карточка проекта — во всю ширину.
    path: '/projects-v2/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <ProjectV2DetailPage /> }],
  },
  {
    path: '/admin/object-types',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ObjectTypesPage /> }],
  },
  {
    path: '/crm',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <CrmV2Page /> }],
  },

  { path: '*', element: <NotFound /> },
];
